// pages/WebsiteLinks/ListWebsites.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useLocation } from "react-router-dom";
import DataTable from 'react-data-table-component';

import {
    Box,
    Typography,
    Alert,
    TextField,
    IconButton,
    CircularProgress,
    Button,
    Tooltip,
    useTheme,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
} from "@mui/material";
import {
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    HourglassEmpty as HourglassEmptyIcon,
    CloudUpload as ScrapeIcon,
} from "@mui/icons-material";

// Refactored ScrapeStatusModal using Material-UI Dialog
const ScrapeStatusModal = ({ isOpen, onRequestClose, scrapeResults }) => {
    return (
        <Dialog open={isOpen} onClose={onRequestClose} fullWidth maxWidth="md">
            <DialogTitle>Scraping Status</DialogTitle>
            <DialogContent dividers>
                <List>
                    {scrapeResults.map((result, index) => (
                        <ListItem key={index} divider>
                            <ListItemIcon>
                                {result.status === 'success' && <CheckCircleIcon color="success" />}
                                {result.status === 'error' && <ErrorIcon color="error" />}
                                {result.status === 'pending' && <HourglassEmptyIcon color="action" />}
                            </ListItemIcon>
                            <ListItemText
                                primary={result.website_link}
                                secondary={result.message}
                                primaryTypographyProps={{ style: { wordBreak: 'break-all' } }}
                            />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onRequestClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const ExpandedRow = ({ data }) => {
    return (
        <Box p={2} sx={{ backgroundColor: 'action.hover' }}>
            <Typography variant="body2">
                <strong>Website Link:</strong> {data.website_link}
            </Typography>
        </Box>
    );
};


export default function ListWebsites({ refresh, setRefresh, chatbotId, permission }) {
    const theme = useTheme();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const fetchedUrl = queryParams.get('fetched_url');

    const [selectionLimit, setSelectionLimit] = useState(50);
    const [userPlan, setUserPlan] = useState('freemium');

    useEffect(() => {
        const fetchUserPlan = async () => {
            try {
                const response = await axiosInstance.get(`/subscriptions/current`);
                const planName = response.data?.plan?.name?.toLowerCase();
                setUserPlan(planName);

                if (planName === 'basic') {
                    setSelectionLimit(200);
                } else if (planName === 'premium') {
                    setSelectionLimit(Number.MAX_SAFE_INTEGER);
                } else {
                    setSelectionLimit(50); // Freemium
                }
            } catch (error) {
                console.error("Error fetching user subscription plan, defaulting to Freemium limit.", error);
                setSelectionLimit(50);
            }
        };
        fetchUserPlan();
    }, []);

    useEffect(() => {
        document.title = 'Website Lists | KlonIT Web Application';
        fetchDocuments();
    }, [refresh]);

    const [documents, setDocuments] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectionError, setSelectionError] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [scrapeModalIsOpen, setScrapeModalIsOpen] = useState(false);
    const [scrapeResults, setScrapeResults] = useState([]);

    const getUpgradeMessage = (plan) => {
        if (plan === 'freemium') {
            return "Upgrade to Basic to select up to 200 (scraped 2 at a time), or Premium to select all (scraped 5 at a time).";
        }
        if (plan === 'basic') {
            return "Upgrade to Premium to select all and scrape 5 URLs at a time for maximum speed.";
        }
        return "There may be a system or browser limitation.";
    };

    const fetchDocuments = async (page = 1, limit = perPage, search = "") => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get(`/get-unscrape-website-links`, {
                params: { page, limit, search, ...(chatbotId ? { chatbot_id: chatbotId } : {}) }
            });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

            const pendingDocuments = responseData.data.filter(doc => doc.scrape_status === 'pending');
            setDocuments(pendingDocuments);
            setTotalRows(responseData.total || 0);
            setCurrentPage(page);
        } catch (error) {
            setError(error.response?.data?.message || error.message || "Failed to fetch website links.");
        } finally {
            setLoading(false);
        }
    };

    const getFileType = (websiteLink) => {
        const lowerCaseLink = websiteLink.toLowerCase();
        if (lowerCaseLink.endsWith('.pdf')) return "PDF";
        if (lowerCaseLink.endsWith('.doc') || lowerCaseLink.endsWith('.docx')) return "Document";
        if (lowerCaseLink.endsWith('.png') || lowerCaseLink.endsWith('.jpg') || lowerCaseLink.endsWith('.jpeg') || lowerCaseLink.endsWith('.gif')) return "Image";
        if (lowerCaseLink.endsWith('.csv') || lowerCaseLink.endsWith('.xlsx') || lowerCaseLink.endsWith('.xls')) return "Structure Document";
        if (lowerCaseLink.startsWith('http') || lowerCaseLink.startsWith('https') || lowerCaseLink.startsWith('www')) return "URL";
        return "Other";
    };

    const getFileTypeColor = (fileType) => {
        switch (fileType) {
            case "PDF": return "error";
            case "URL": return "success";
            case "Document": return "primary";
            case "Image": return "secondary";
            case "Structure Document": return "warning";
            default: return "default";
        }
    };

    const handleScrapeDataBTN = async () => {
        if (selectedDocuments.length === 0) {
            setError("No documents selected for scraping.");
            return;
        }

        setFetching(true);
        setError(null);
        setScrapeModalIsOpen(true);

        const params = chatbotId ? { chatbot_id: chatbotId } : {};
        let batchSize = userPlan === 'premium' ? 5 : userPlan === 'basic' ? 2 : 1;

        const allDocsToProcess = selectedDocuments.map(doc => ({
            id: doc.id,
            website_link: doc.website_link,
            status: 'pending',
            message: 'Waiting...'
        }));
        setScrapeResults(allDocsToProcess);
        let currentScrapeResults = [...allDocsToProcess];

        for (let i = 0; i < allDocsToProcess.length; i += batchSize) {
            const chunk = allDocsToProcess.slice(i, i + batchSize);
            const promises = chunk.map(doc =>
                axiosInstance.post(`/add-scrape-data`, { data: [[doc.id], [doc.website_link]] }, { params })
            );
            const results = await Promise.allSettled(promises);

            results.forEach((result, indexInChunk) => {
                const globalIndex = i + indexInChunk;
                if (result.status === 'fulfilled') {
                    const responseData = typeof result.value.data.data === 'string' ? JSON.parse(result.value.data.data) : result.value.data.data;
                    if (responseData?.scrape_data?.[0]?.page_content) {
                        currentScrapeResults[globalIndex].status = 'success';
                        currentScrapeResults[globalIndex].message = 'Scraped and trained successfully.';
                    } else {
                        currentScrapeResults[globalIndex].status = 'error';
                        currentScrapeResults[globalIndex].message = responseData?.scrape_data?.[0]?.error || 'Scraping failed: No content returned.';
                    }
                } else {
                    currentScrapeResults[globalIndex].status = 'error';
                    currentScrapeResults[globalIndex].message = result.reason.response?.data?.message || result.reason.message || 'A network or server error occurred.';
                }
            });
            setScrapeResults([...currentScrapeResults]);
        }

        setFetching(false);
        setSelectedDocuments([]);
        setToggleCleared(!toggleCleared);
        setRefresh((prev) => !prev);

        const failureCount = currentScrapeResults.filter(res => res.status === 'error').length;
        const successCount = currentScrapeResults.length - failureCount;
        if (failureCount > 0) {
            setError(`${successCount} URL(s) scraped successfully, while ${failureCount} failed. Check the status modal for details.`);
        } else {
            setError("All selected URLs were successfully scraped and are now being trained.");
        }
    };
    
    useEffect(() => {
        fetchDocuments(1, 10, searchText);
    }, [refresh, searchText]);

    const handleBulkDelete = async () => {
        if (selectedDocuments.length === 0) {
            setError("No documents selected for deletion.");
            return;
        }
        try {
            const params = chatbotId ? { chatbot_id: chatbotId } : {};
            await Promise.all(selectedDocuments.map(doc => axiosInstance.delete(`/delete-website-link/${doc.id}`, { params })));
            setDocuments(documents.filter(doc => !selectedDocuments.some(selected => selected.id === doc.id)));
            setSelectedDocuments([]);
            setToggleCleared(!toggleCleared);
            setError("Selected website links have been deleted successfully.");
        } catch (error) {
            setError(error.message);
        }
    };

    const handleDelete = async (documentId) => {
        try {
            const params = chatbotId ? { chatbot_id: chatbotId } : {};
            await axiosInstance.delete(`/delete-website-link/${documentId}`, { params });
            setDocuments(documents.filter(doc => doc.id !== documentId));
            setError("Website link has been deleted successfully.");
        } catch (error) {
            setError(error.message);
        }
    };

    const handleView = (url) => window.open(url, '_blank');

    const handleChange = ({ selectedRows }) => {
        if (selectedRows.length > selectionLimit) {
            const upgradeText = getUpgradeMessage(userPlan);
            setSelectionError(`Your selection limit is ${selectionLimit} URLs at a time. ${upgradeText}`);
            setSelectedDocuments(selectedRows.slice(0, selectionLimit));
            setToggleCleared(!toggleCleared); // Force re-render to clear extra selections
        } else {
            setSelectedDocuments(selectedRows);
            setSelectionError(null);
        }
    };
    
    const customDataTableStyles = {
        table: { style: { backgroundColor: theme.palette.background.paper, }, },
        headRow: { style: { backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9", borderBottom: `1px solid ${ theme.palette.mode === "dark" ? "#334155" : "#cbd5e1" }`, }, },
        headCells: { style: { fontWeight: 600, fontSize: "14px", color: theme.palette.text.primary, }, },
        rows: { style: { backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, transition: "background-color 0.2s ease", "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc", }, }, },
        pagination: { style: { backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, borderTop: `1px solid ${ theme.palette.mode === "dark" ? "#334155" : "#e2e8f0" }`, }, },
        expanderRow: { style: { backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb", }, },
    };

    const columns = [
        { name: 'Website Link', selector: row => row.website_link, sortable: true, wrap: true, grow: 2 },
        { name: 'File Type', selector: row => getFileType(row.website_link), sortable: true, width: '120px', cell: row => { const fileType = getFileType(row.website_link); return <Chip label={fileType} color={getFileTypeColor(fileType)} size="small" />; }, hide: 'sm' },
        { name: 'Is Scrape?', selector: row => row.is_scrape ? "Yes" : "No", sortable: true, width: '100px', hide: 'sm' },
        { name: 'Create Date', selector: row => row.created_date, sortable: true, width: '120px', hide: 'md' },
        { name: 'Action', width: '100px', cell: row => ( <Box> <Tooltip title="View Website Link"> <IconButton size="small" onClick={() => handleView(row.website_link)}><VisibilityIcon fontSize="small" /></IconButton> </Tooltip> {permission.canDelete && ( <Tooltip title="Delete Website Link"> <IconButton size="small" onClick={() => handleDelete(row.id)} color="error"><DeleteIcon fontSize="small" /></IconButton> </Tooltip> )} </Box> ), },
    ];
    
    return (
        <Box>
            <Card>
                <CardContent>
                    <Grid container alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={8}>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                                {permission.canDelete && <Button variant="outlined" color="error" onClick={handleBulkDelete} disabled={selectedDocuments.length === 0 || fetching} startIcon={fetching ? <CircularProgress size={16} /> : <DeleteIcon />} size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Delete ({selectedDocuments.length})</Button>}
                                {permission.canModify && <Button variant="outlined" color="primary" onClick={handleScrapeDataBTN}  disabled={selectedDocuments.length === 0 || fetching} startIcon={fetching ? <CircularProgress size={16} /> : <ScrapeIcon />} size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Scrape & Train ({selectedDocuments.length})</Button>}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth variant="outlined" size="small" placeholder="Search URL, Title..." value={searchText} onChange={(e) => setSearchText(e.target.value)} /></Grid>
                    </Grid>

                    {fetchedUrl && <Alert severity="info" sx={{ mb: 2 }}>Please select URLs to fetch the data.</Alert>}
                    {selectionError && <Alert severity="error" sx={{ mb: 2 }}>{selectionError}</Alert>}
                    {error && <Alert severity={error.includes("successfully") ? "success" : "warning"} sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}


                    {loading ? (
                        <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                            <CircularProgress />
                            <Typography mt={2}>Loading Website Links...</Typography>
                        </Box>
                    ) : documents.length === 0 ? (
                        <Box textAlign="center" py={4}>
                           <Typography variant="h6" color="text.secondary">Add website links to start crawling...</Typography>
                        </Box>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={documents}
                            pagination
                            paginationServer
                            paginationDefaultPage={currentPage}
                            paginationTotalRows={totalRows}
                            paginationPerPage={perPage}
                            onChangePage={(page) => fetchDocuments(page, perPage, searchText)}
                            onChangeRowsPerPage={(newPerPage, page) => {
                                setPerPage(newPerPage);
                                fetchDocuments(page, newPerPage, searchText);
                            }}
                            selectableRows
                            onSelectedRowsChange={handleChange}
                            clearSelectedRows={toggleCleared}
                            expandableRows
                            expandableRowsComponent={ExpandedRow}
                            customStyles={customDataTableStyles}
                            theme={theme.palette.mode === 'dark' ? 'dark' : 'default'}
                        />
                    )}
                </CardContent>
            </Card>
            <ScrapeStatusModal
                isOpen={scrapeModalIsOpen}
                onRequestClose={() => setScrapeModalIsOpen(false)}
                scrapeResults={scrapeResults}
            />
        </Box>
    );
}